// §D13 (Section 16) — manager queue of IN_TRANSIT_EXCEPTION returns. The list rows carry the
// transit-exception fields (BE contract phase 3 §3), so no per-row GET is needed to render the
// queue; the full detail (version, evidence photos) is fetched only for the row being resolved.
// Resolve opens the Section 17 dialog. Gated by RETURN_RESOLVE_TRANSIT.

import { Anchor, Box, Group, Pagination, Progress, Select, Stack, Table, Text } from "@mantine/core";
import { i18n } from "@dehub/theme";
import { EyeIcon, ShieldCheckIcon } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import useSWR from "swr";
import { ReturnApi } from "../../../apis";
import type { IReturnDetail, IReturnSummary } from "../../../apis/return";
import { DeliveryRowAction } from "../../../components/delivery/action-controls";
import { DotPill, TableFrame } from "../../../components/delivery/atoms";
import { PageLayout } from "../../../components/layout";
import { usePermission } from "../../../components/permission";
import { ReturnKPICards } from "../../../components/return/kpi-cards";
import { useReturnActions } from "../../../components/return/return-actions";
import { TransitExceptionResolveModal } from "../../../components/return/transit-exception-resolve-modal";
import { DeniedCard, ListState } from "../../../components/ui";
import { message } from "../../../utils/message";
import { dateTimeSecFormat } from "../../../utils/date";

type TFn = (k: string, o?: Record<string, unknown>) => string;

const breadcrumb = () => [
  { to: "/", label: i18n.t("returns:breadcrumb.home") },
  { to: "/returns", label: i18n.t("returns:breadcrumb.returns") },
  { label: i18n.t("returns:transitExceptions.title") }
];

const SLA_DAYS = 14;
const PAGE_LIMIT = 50;

const EXCEPTION_TYPE_CODES = ["LOST", "DAMAGED_IN_TRANSIT", "WRONG_DESTINATION", "STOLEN"];
const REPORTED_BY_CODES = ["CARRIER", "MANAGER", "BUYER"];

const exceptionTypeLabel = (t: TFn, code?: string | null): string =>
  code ? t(`transitResolve.exceptionType.${code}`) : "-";
const reportedByLabel = (t: TFn, code?: string | null): string =>
  code ? t(`transitResolve.reportedBy.${code}`) : "-";

const exceptionTypeOptions = (t: TFn) => [
  { value: "ALL", label: t("transitExceptions.allTypes") },
  ...EXCEPTION_TYPE_CODES.map((value) => ({ value, label: t(`transitResolve.exceptionType.${value}`) }))
];
const reportedByOptions = (t: TFn) => [
  { value: "ALL", label: t("transitExceptions.allSources") },
  ...REPORTED_BY_CODES.map((value) => ({ value, label: t(`transitResolve.reportedBy.${value}`) }))
];

export function TransitExceptionsPage (){
  const { t } = useTranslation("returns");
  const navigate = useNavigate();
  const { hasAccess } = usePermission("RETURN_RESOLVE_TRANSIT");
  const { permissionState } = useReturnActions();
  const resolveState = permissionState("resolveTransit");

  const [typeFilter, setTypeFilter] = useState("ALL");
  const [reportedByFilter, setReportedByFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<IReturnDetail | null>(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  // One request per page: the list row carries the exception fields (O153).
  const { data, isLoading, error, mutate } = useSWR(
    hasAccess ? ["returns.transit-exceptions", page] : null,
    () => ReturnApi.list({
      status: ["IN_TRANSIT_EXCEPTION"], page, limit: PAGE_LIMIT, sortBy: "requestedAt", sortDir: "desc"
    }),
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const loaded = data?.returns ?? [];
  const total = data?.pagination.total ?? 0;
  const totalPages = data?.pagination.totalPages ?? 1;

  const rows = useMemo(() => {
    let r = loaded;
    if (typeFilter !== "ALL") r = r.filter((x) => x.transitExceptionType === typeFilter);
    if (reportedByFilter !== "ALL") r = r.filter((x) => x.transitExceptionReportedBy === reportedByFilter);
    return r;
  }, [loaded, typeFilter, reportedByFilter]);

  const breachCount = loaded.filter((x) => x.slaDaysRemaining != null && x.slaDaysRemaining <= 0).length;
  const filtered = typeFilter !== "ALL" || reportedByFilter !== "ALL";

  // Only the row being resolved needs the full detail (version + evidence photos).
  const onResolve = async (row: IReturnSummary) => {
    try {
      setLoadingId(row.returnRequestId);
      const res = await ReturnApi.get(row.returnRequestId);
      setSelected(res.returnRequest);
      setResolveOpen(true);
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setLoadingId(null);
    }
  };

  if (!hasAccess) {
    return (
      <PageLayout title={t("transitExceptions.title")} breadcrumb={breadcrumb()}>
        <DeniedCard permission="RETURN_RESOLVE_TRANSIT" />
      </PageLayout>
    );
  }

  const footer = (
    <>
      <Text span ff="monospace" fz="var(--nw-fz-mono)">{loaded.length ? `${(page - 1) * PAGE_LIMIT + 1}-${(page - 1) * PAGE_LIMIT + loaded.length} / ${total}` : `0 / ${total}`}</Text>
      {totalPages > 1 ? <Pagination value={page} total={totalPages} onChange={setPage} size="sm" /> : <Box />}
    </>
  );

  return (
    <PageLayout
      title={t("transitExceptions.title")}
      subTitle={t("transitExceptions.subtitle")}
      breadcrumb={breadcrumb()}
      loading={isLoading && !data}
    >
      <Stack gap="md">
        <ReturnKPICards
          kpis={[
            { label: t("prevention.list.tabActive"), value: total, hint: t("transitExceptions.activeHint"), tone: "red" },
            { label: t("transitExceptions.slaBreached"), value: breachCount, hint: t("transitExceptions.slaHint", { count: SLA_DAYS }), tone: "amber" }
          ]}
        />

        <Group gap="sm" align="flex-end">
          <Select label={t("shippingPanel.exceptionTypeLabel")} data={exceptionTypeOptions(t)} value={typeFilter} onChange={(v) => setTypeFilter(v ?? "ALL")} allowDeselect={false} w={220} />
          <Select label={t("transitResolve.reportedByLabel")} data={reportedByOptions(t)} value={reportedByFilter} onChange={(v) => setReportedByFilter(v ?? "ALL")} allowDeselect={false} w={220} />
        </Group>

        {total > PAGE_LIMIT ? (
          <Text size="sm" c="var(--nw-text-2)">{t("transitExceptions.moreThanPage", { limit: PAGE_LIMIT, total })}</Text>
        ) : null}

        <TableFrame footer={rows.length > 0 || total > 0 ? footer : undefined}>
          {error && !data ? (
            <ListState state="error" title={t("transitExceptions.loadError")} onRetry={() => mutate()} errorCode={(error as { statusCode?: number })?.statusCode} />
          ) : rows.length === 0 ? (
            isLoading ? (
              <ListState state="loading" columns={8} />
            ) : (
              <ListState
                state={filtered ? "noResults" : "empty"}
                icon={filtered ? undefined : <ShieldCheckIcon size={24} />}
                title={filtered ? t("listTable.noResults") : t("transitExceptions.empty")}
                description={filtered ? t("listTable.noResultsHint") : t("transitExceptions.emptyHint")}
                action={filtered ? { onClick: () => { setTypeFilter("ALL"); setReportedByFilter("ALL"); } } : undefined}
              />
            )
          ) : (
            <Table.ScrollContainer minWidth={980}>
              <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" styles={{ td: { verticalAlign: "middle" } }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("listTable.col.action")}</Table.Th>
                    <Table.Th>{t("listTable.col.retNumber")}</Table.Th>
                    <Table.Th>{t("transitExceptions.col.exception")}</Table.Th>
                    <Table.Th>{t("transitResolve.carrier")}</Table.Th>
                    <Table.Th>{t("transitResolve.reportedByLabel")}</Table.Th>
                    <Table.Th>{t("detail.field.date")}</Table.Th>
                    <Table.Th>{t("transitExceptions.col.sla")}</Table.Th>
                    <Table.Th>{t("filters.buyer")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((d) => {
                    const remaining = d.slaDaysRemaining;
                    const used = remaining != null ? Math.min(SLA_DAYS, Math.max(0, SLA_DAYS - remaining)) : null;
                    const overdue = remaining != null && remaining <= 0;
                    return (
                      <Table.Tr key={d.returnRequestId}>
                        <Table.Td>
                          <Group gap="xs" wrap="nowrap">
                            <DeliveryRowAction
                              label={t("transitResolve.resolve")}
                              icon={<ShieldCheckIcon />}
                              state={{ allowed: resolveState.enabled, reason: resolveState.reason }}
                              loading={loadingId === d.returnRequestId}
                              onClick={() => onResolve(d)}
                            />
                            <DeliveryRowAction label={t("breadcrumb.detail")} icon={<EyeIcon />} onClick={() => navigate(`/returns/${d.returnRequestId}`)} />
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Anchor component={Link} to={`/returns/${d.returnRequestId}`} ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text)" translate="no" style={{ whiteSpace: "nowrap" }}>
                            {d.returnNumber}
                          </Anchor>
                        </Table.Td>
                        <Table.Td>
                          <DotPill tone="red">{exceptionTypeLabel(t, d.transitExceptionType)}</DotPill>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{d.pickupCarrier || "-"}</Text>
                          {d.transitExceptionCarrierRef ? (
                            <Text size="sm" c="var(--nw-text-2)" ff="monospace" fz="var(--nw-fz-mono)" translate="no">{d.transitExceptionCarrierRef}</Text>
                          ) : null}
                        </Table.Td>
                        <Table.Td><Text size="sm">{reportedByLabel(t, d.transitExceptionReportedBy)}</Text></Table.Td>
                        <Table.Td>
                          <Text size="sm" ff="monospace" fz="var(--nw-fz-mono)" style={{ whiteSpace: "nowrap" }}>{dateTimeSecFormat(d.transitExceptionReportedAt)}</Text>
                        </Table.Td>
                        <Table.Td>
                          {used != null ? (
                            <Box w={140}>
                              <Text size="sm" c="var(--nw-text-2)" ff="monospace" fz="var(--nw-fz-mono)" fw={overdue ? 600 : 400}>
                                {overdue ? t("listTable.daysOverdue", { count: Math.abs(remaining ?? 0) }) : `${used} / ${SLA_DAYS}`}
                              </Text>
                              <Progress value={Math.min(100, (used / SLA_DAYS) * 100)} color={overdue ? "red" : "gray"} size="sm" radius="sm" mt={4} aria-label={t("transitExceptions.col.sla")} />
                            </Box>
                          ) : <Text size="sm">-</Text>}
                        </Table.Td>
                        <Table.Td maw={220}><Text size="sm" lineClamp={2}>{d.buyerBusinessName || "-"}</Text></Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </TableFrame>
      </Stack>

      <TransitExceptionResolveModal
        detail={selected}
        opened={resolveOpen}
        onClose={() => setResolveOpen(false)}
        onResolved={() => mutate()}
      />
    </PageLayout>
  );
}
